import Bugsnag from "@bugsnag/js";
import CustomizationContainer from "kustom-front/src/containers/customization/CustomizationContainer";
import KustomApiProvider from "kustom-front/src/providers/customization/KustomApiProvider";
import UploadSelectionProvider from "kustom-front/src/providers/selection/UploadSelectionProvider";
import FotomUploadProvider from "kustom-front/src/providers/upload/FotomUploadProvider";
import {
  Configuration,
  ConfigurationCallbackInput,
  OptionsConfiguration,
  PageName,
} from "kustom-front/src/types/customization/configuration";
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import NotificationsArea from "@Components/molecules/cart/NotificationsArea";
import LoginModal from "@Components/molecules/LoginModal";
import NotificationsProvider from "@Components/NotificationsStateProvider";
import Upsells from "@Components/organisms/customization/Upsells";
import WithGlobalLoader, { useGlobalLoader } from "@Components/WithGlobalLoader";
import CheerzApiProvider from "@Providers/CheerzApiProvider";
import {
  CheerzApiCustomizationProduct,
  CheerzApiCustomizationSelectedProductOptions,
} from "@Types/api/customization";
import { ApiErrorResponse } from "@Types/api/error";
import { Currency, Price } from "@Types/api/price";
import { CheerzApiProvidersWithFotom } from "@Types/api/providers";
import { Upsell, UpsellIdentifiers } from "@Types/upsells";
import { getCsrfToken, getMetaContent } from "@Utils/meta";
import { goToCart } from "@Utils/navigation";
import { connectToProvider } from "@Utils/providers";
import { trackKustom } from "@Utils/track";
import { convertUpsellsFromApiToContent, formatSelectedOptions } from "@Utils/upsells";
import { convertUserFromApiToContent } from "@Utils/user";

interface Props {
  customizationId: string;
  localizationSlug: string;
}

type ProviderTags =
  | "facebook"
  | "friends_sharing"
  | "google_photos"
  | "instagram"
  | "mobile_upload"
  | "unsplash"
  | "upload";

const CustomizationEditPage: FunctionComponent<Props> = ({ customizationId, localizationSlug }) => {
  const [templateRevisionId, setTemplateRevisionId] = useState<string>();
  const [contentId, setContentId] = useState<string>();
  const [pagesIncrement, setPagesIncrement] = useState<number>();
  const [pagesByLineMax, setPagesByLineMax] = useState<number>();
  const [pagesByLineMin, setPagesByLineMin] = useState<number>();
  const [pagesMin, setPagesMin] = useState<number>();
  const [pagesMax, setPagesMax] = useState<number>();
  const [kustomToken, setKustomToken] = useState<string>();
  const [productPrice, setProductPrice] = useState<Price>();
  const [additionalPrice, setAdditionalPrice] = useState<Price>();
  const [providersWithFotom, setProvidersWithFotom] = useState<CheerzApiProvidersWithFotom>();
  const [product, setProduct] = useState<CheerzApiCustomizationProduct>();
  const [currency, setCurrency] = useState<Currency>("EUR");
  const [displayUpsells, setDisplayUpsells] = useState<boolean>(false);
  const [productTag, setProductTag] = useState<string>();
  const [upsells, setUpsells] = useState<Upsell[]>([]);
  const [selectedProductOptions, setSelectedProductOptions] =
    useState<CheerzApiCustomizationSelectedProductOptions>();
  const [characteristics, setCharacteristics] = useState<string[]>([]);
  const [displayLoginModal, setDisplayLoginModal] = useState<boolean>(false);
  const [fotomKey, setFotomKey] = useState<string>("");
  const [guest, setGuest] = useState<boolean>(true);
  const [inCart, setInCart] = useState<boolean>(false);
  const [withZoomOut, setWithZoomOut] = useState<boolean>(false);
  const [saveCallback, setSaveCallback] = useState<(() => Promise<void>) | undefined>();

  const { setLoading } = useGlobalLoader();
  const intl = useIntl();

  const cheerzApiProvider = useMemo(() => {
    const csrfToken = getMetaContent("csrf-token");
    if (!csrfToken) {
      throw new Error("CSRF Token not found");
    }

    return new CheerzApiProvider(localizationSlug, csrfToken);
  }, [localizationSlug]);

  const mountage = useRef<boolean>(false);

  useEffect(() => {
    mountage.current = true;
    return () => {
      mountage.current = false;
    };
  }, [mountage]);

  const getProvidersWithFotom = useCallback(() => {
    return cheerzApiProvider.getProviders().then((providersResponse) => {
      // Hack as it uses sessions
      providersResponse.providers.push({ provider: "upload", token: "1" });
      providersResponse.providers.push({ provider: "mobile_upload", token: "1" });
      return providersResponse;
    });
  }, [cheerzApiProvider]);

  const kustomApiProvider = useMemo(() => {
    return new KustomApiProvider(kustomToken);
  }, [kustomToken]);

  const reload = useCallback(() => {
    const doIt = async () => {
      const customizationResponse = await cheerzApiProvider.getCustomization(customizationId);

      setPagesIncrement(customizationResponse.kustom_pages_increment);
      setPagesByLineMax(customizationResponse.pages_by_line_max);
      setPagesByLineMin(customizationResponse.pages_by_line_min);
      setPagesMin(customizationResponse.kustom_pages_min);
      setPagesMax(customizationResponse.kustom_pages_max);
      setProduct(customizationResponse.product);
      setProductTag(customizationResponse.product.tag);
      setProductPrice(customizationResponse.product.product_price);
      setAdditionalPrice(customizationResponse.product.additional_price);
      setCurrency(customizationResponse.product.product_price.currency);
      setCharacteristics(customizationResponse.product.characteristics);
      setSelectedProductOptions(customizationResponse.options_hash);
      setInCart(customizationResponse.in_cart);
      setWithZoomOut(customizationResponse.product.with_dezoom);
    };

    return doIt();
  }, [
    cheerzApiProvider,
    customizationId,
    setAdditionalPrice,
    setCurrency,
    setPagesIncrement,
    setPagesByLineMax,
    setPagesByLineMin,
    setPagesMax,
    setPagesMin,
    setProduct,
    setProductPrice,
    setProductTag,
    setSelectedProductOptions,
  ]);

  useEffect(() => {
    let isStillSubscribed = true;
    setLoading(true);

    const doIt = async () => {
      const customizationResponse = await cheerzApiProvider.getCustomization(customizationId);
      const userResponse = await cheerzApiProvider.getUser();

      if (isStillSubscribed) {
        setPagesIncrement(customizationResponse.kustom_pages_increment);
        setPagesByLineMax(customizationResponse.pages_by_line_max);
        setPagesByLineMin(customizationResponse.pages_by_line_min);
        setPagesMin(customizationResponse.kustom_pages_min);
        setPagesMax(customizationResponse.kustom_pages_max);
        setKustomToken(customizationResponse.kustom_user_token);
        setProduct(customizationResponse.product);
        setProductTag(customizationResponse.product.tag);
        setProductPrice(customizationResponse.product.product_price);
        setAdditionalPrice(customizationResponse.product.additional_price);
        setCurrency(customizationResponse.product.product_price.currency);
        setCharacteristics(customizationResponse.product.characteristics);
        setSelectedProductOptions(customizationResponse.options_hash);
        setInCart(customizationResponse.in_cart);
        setWithZoomOut(customizationResponse.product.with_dezoom);

        const userContent = convertUserFromApiToContent(userResponse);
        setFotomKey(userContent.fotomKey);
        setGuest(userContent.isGuest);
      }

      const providersResponse = await getProvidersWithFotom();

      if (isStillSubscribed) {
        setProvidersWithFotom(providersResponse);

        if (
          customizationResponse.kustom_template_revision_ref &&
          customizationResponse.kustom_content_ref
        ) {
          setContentId(customizationResponse.kustom_content_ref);
          setTemplateRevisionId(customizationResponse.kustom_template_revision_ref);
          setLoading(false);
        }
      }
    };

    void doIt();

    return () => {
      isStillSubscribed = false;
    };
  }, [
    cheerzApiProvider,
    customizationId,
    getProvidersWithFotom,
    kustomApiProvider,
    setAdditionalPrice,
    setContentId,
    setCurrency,
    setKustomToken,
    setLoading,
    setPagesMax,
    setPagesIncrement,
    setPagesByLineMax,
    setPagesByLineMin,
    setPagesMin,
    setProduct,
    setProductPrice,
    setProductTag,
    setProvidersWithFotom,
    setTemplateRevisionId,
    setSelectedProductOptions,
  ]);

  useEffect(() => {
    const doIt = async (productTag: string) => {
      const cheerzApiV2Provider = new CheerzApiProvider(localizationSlug, getCsrfToken(), true);
      const settingsReponse = await cheerzApiV2Provider.getSettings(localizationSlug);
      const optionsResponse = await cheerzApiV2Provider.getOptions(
        settingsReponse.response,
        productTag
      );
      setUpsells(convertUpsellsFromApiToContent(optionsResponse.product_options));
    };
    if (!!productTag) {
      void doIt(productTag);
    }
  }, [productTag]);

  const hideUpsells = () => {
    setDisplayUpsells(false);
  };

  const upsellsModalSubmit = (selectedUpsells: UpsellIdentifiers[]) => {
    cheerzApiProvider
      .updateArticle(customizationId, {
        options: formatSelectedOptions(selectedUpsells, upsells),
      })
      .then(() => {
        return cheerzApiProvider.addArticle(customizationId);
      })
      .then(() => {
        setDisplayUpsells(false);
        goToCart(localizationSlug);
      })
      .catch((error: ApiErrorResponse) => {
        Bugsnag.notify(
          error.response?.data?.error_description[0]
            ? `${error.response?.data?.error_description[0]}`
            : "Error on updateArticle call"
        );
      });
  };

  const onFinalize = useCallback(async () => {
    if (!contentId) {
      return;
    }

    if (upsells.length > 0) {
      setDisplayUpsells(true);
    } else {
      setLoading(true);

      await cheerzApiProvider.addArticle(customizationId);

      goToCart(localizationSlug);
    }
  }, [cheerzApiProvider, contentId, customizationId, localizationSlug, upsells, setLoading]);

  const onSave = useCallback(
    (callback: () => Promise<void>) => {
      setDisplayLoginModal(true);
      setSaveCallback(() => callback);
    },
    [setDisplayLoginModal, setSaveCallback]
  );

  const isFinalizeActive = useCallback(
    ({ pagesCount }: ConfigurationCallbackInput) => {
      const increment = pagesIncrement ? pagesIncrement : 1;
      const min = pagesMin ? pagesMin : 0;
      const max = pagesMax ? pagesMax : 999999;

      return min <= pagesCount && pagesCount <= max && pagesCount % increment === 0;
    },
    [pagesIncrement, pagesMax, pagesMin]
  );

  const kustomPageName: PageName | undefined = useMemo(() => {
    if (product?.kustom_page_name_plural && product?.kustom_page_name_singular) {
      return {
        plural: product.kustom_page_name_plural,
        singular: product.kustom_page_name_singular,
      };
    }
    return undefined;
  }, [product]);

  const summaryComponent: FunctionComponent<{ input: ConfigurationCallbackInput }> = useMemo(() => {
    return ({ input: { pagesCount } }) => {
      if (!productPrice) {
        return null;
      }

      const priceCents = productPrice.cents;

      let additionalCents = 0;
      if (additionalPrice && pagesMin && pagesCount > pagesMin) {
        if (pagesIncrement && pagesIncrement > 1) {
          additionalCents =
            Math.ceil((pagesCount - pagesMin) / pagesIncrement) * additionalPrice.cents;
        } else {
          additionalCents = (pagesCount - pagesMin) * additionalPrice.cents;
        }
      }

      const totalCents = priceCents + additionalCents;
      const totalFormated = intl.formatNumber(totalCents / 100, {
        currency: productPrice.currency,
        style: "currency",
      });

      return <div>{totalFormated}</div>;
    };
  }, [additionalPrice, intl, pagesIncrement, pagesMin, productPrice]);

  const uploadProvider = useMemo(() => {
    if (!providersWithFotom) {
      return;
    }

    return new FotomUploadProvider(
      providersWithFotom.fotomHost,
      providersWithFotom.fotomUserId,
      providersWithFotom.fotomUserToken
    );
  }, [providersWithFotom]);

  const selectionUploadProvider = useMemo(() => {
    if (!providersWithFotom) {
      return;
    }

    return new UploadSelectionProvider(
      providersWithFotom.fotomHost,
      providersWithFotom.fotomUserId,
      providersWithFotom.fotomUserToken,
      fotomKey
    );
  }, [fotomKey, providersWithFotom]);

  const selectionProviderConnect = useCallback(
    (providerTag: ProviderTags) => {
      if (
        providerTag !== "facebook" &&
        providerTag !== "instagram" &&
        providerTag !== "google_photos"
      ) {
        throw new Error(`Unsupported provider ${providerTag}`);
      }

      return connectToProvider(providerTag).then((token) => {
        if (!mountage.current) {
          return;
        }

        if (token) {
          localStorage.setItem(providerTag, token);
        }

        getProvidersWithFotom()
          .then((providersResponse) => {
            if (!mountage.current) {
              return;
            }

            setProvidersWithFotom(providersResponse);
          })
          .catch((error: ApiErrorResponse) => {
            Bugsnag.notify(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on getProvidersWithFotom call"
            );
          });
      });
    },
    [getProvidersWithFotom, mountage, setProvidersWithFotom]
  );

  const selectionProviderDisconnect = useCallback(
    (providerTag: ProviderTags) => {
      if (
        providerTag !== "facebook" &&
        providerTag !== "instagram" &&
        providerTag !== "google_photos"
      ) {
        throw new Error(`Unsupported provider ${providerTag}`);
      }
      return cheerzApiProvider.deleteProvider(providerTag).then(() => {
        getProvidersWithFotom()
          .then((providersResponse) => {
            if (!mountage.current) {
              return;
            }

            setProvidersWithFotom(providersResponse);
          })
          .catch((error: ApiErrorResponse) => {
            Bugsnag.notify(
              error.response?.data?.error_description[0]
                ? `${error.response?.data?.error_description[0]}`
                : "Error on getProvidersWithFotom call"
            );
          });
      });
    },
    [getProvidersWithFotom, mountage, setProvidersWithFotom, cheerzApiProvider]
  );

  const options: OptionsConfiguration[] = useMemo(() => {
    const builtOptions: OptionsConfiguration[] = [];

    if (product) {
      const switchProducts =
        product.switch_products &&
        product.switch_products.filter(
          (switchProduct) =>
            switchProduct.kustom_template_revision_ref === product.kustom_template_revision_ref
        );

      if (switchProducts.length > 0 || product.product_switch_label) {
        const currentValue = {
          label: product.product_switch_label,
          price: intl.formatMessage({ id: "cheerz_CustomizationEditPage_included" }),
          value: product.tag,
        };

        const onChange = (newValue: string) => {
          if (newValue === currentValue.value) {
            return;
          }

          cheerzApiProvider
            .updateArticle(customizationId, {
              product_tag: newValue,
            })
            .then(() => {
              void reload();
            })
            .catch((error: ApiErrorResponse) => {
              Bugsnag.notify(
                error.response?.data?.error_description[0]
                  ? `${error.response?.data?.error_description[0]}`
                  : "Error on updateArticle call"
              );
            });
        };

        const switchProductValues = switchProducts.map((switchProduct) => {
          const label = switchProduct.product_switch_label;
          let price;
          if (switchProduct.price.cents !== product.product_price.cents) {
            const extraCents = switchProduct.price.cents - product.product_price.cents;
            const extraFormatted = intl.formatNumber(extraCents / 100, {
              currency: product.product_price.currency,
              style: "currency",
            });

            price = `${extraCents > 0 ? "+" : ""}${extraFormatted}`;
          } else {
            price = intl.formatMessage({ id: "cheerz_CustomizationEditPage_included" });
          }

          return {
            label,
            price,
            value: switchProduct.tag,
          };
        });

        builtOptions.push({
          onChange,
          tag: "product_tag",
          value: product.tag,
          values: [currentValue, ...switchProductValues],
        });
      }
    }

    return builtOptions;
  }, [cheerzApiProvider, customizationId, intl, product, reload]);

  const unitAdditionnalPrice: Price | undefined = useMemo(() => {
    if (additionalPrice && pagesIncrement) {
      return { ...additionalPrice, cents: additionalPrice.cents / pagesIncrement };
    }
    return additionalPrice;
  }, [additionalPrice, pagesIncrement]);

  const configuration: Configuration = useMemo(() => {
    return {
      additionalPrice: unitAdditionnalPrice,
      characteristics,
      finalize: {
        isActive: isFinalizeActive,
        label: intl.formatMessage({ id: "cheerz_addToCart" }),
        onFinalize,
      },
      inCart,
      isGuest: guest,
      kustomApiToken: kustomToken,
      onSave,
      options,
      pageName: kustomPageName,
      pages: {
        increment: pagesIncrement,
        max: pagesMax,
        min: pagesMin,
      },
      selectionProviderConnect,
      selectionProviderDisconnect,
      selectionProviderTokens: providersWithFotom ? providersWithFotom.providers : [],
      selectionUploadProvider,
      summaryComponent,
      ui: {
        maxPagesByLine: pagesByLineMax,
        minPagesByLine: pagesByLineMin,
      },
      uploadProvider,
      withZoomOut: withZoomOut,
    };
  }, [
    characteristics,
    intl,
    isFinalizeActive,
    kustomPageName,
    kustomToken,
    onFinalize,
    onSave,
    options,
    pagesIncrement,
    pagesByLineMax,
    pagesByLineMin,
    pagesMax,
    pagesMin,
    providersWithFotom,
    selectionProviderConnect,
    selectionProviderDisconnect,
    summaryComponent,
    unitAdditionnalPrice,
    selectionUploadProvider,
    uploadProvider,
    guest,
  ]);

  const trackDetails = { openTime: Date.now() };
  const trackKustomWithContext = useCallback(
    (name: string): Promise<void> => {
      if (!product) {
        return Promise.resolve();
      }
      return trackKustom(name, product.tag, trackDetails);
    },
    [product, trackDetails]
  );

  if (!contentId || !templateRevisionId || !currency || !productTag) {
    return null;
  }

  const handleQuitCustomization = () => {
    if (document.referrer.search(/\/choices$/i) !== -1) {
      window.history.go(-2);
    } else {
      window.history.back();
    }
  };

  return (
    <div>
      <CustomizationContainer
        configuration={configuration}
        contentId={contentId}
        onQuit={handleQuitCustomization}
        templateRevisionId={templateRevisionId}
        track={trackKustomWithContext}
      />
      <LoginModal
        action="signUp"
        callback={saveCallback}
        display={displayLoginModal}
        hide={() => setDisplayLoginModal(false)}
      />
      <div className="Header-notifications">
        <NotificationsArea />
      </div>
      {upsells && selectedProductOptions ? (
        <Upsells
          displayUpsells={displayUpsells}
          hideUpsells={hideUpsells}
          onSubmit={upsellsModalSubmit}
          selectedUpsells={selectedProductOptions}
          upsells={upsells}
        />
      ) : null}
    </div>
  );
};

const CustomizationEditPageContainer: FunctionComponent<Props> = ({
  customizationId,
  localizationSlug,
}) => {
  return (
    <WithGlobalLoader initialLoading={true}>
      <NotificationsProvider>
        <CustomizationEditPage
          customizationId={customizationId}
          localizationSlug={localizationSlug}
        />
      </NotificationsProvider>
    </WithGlobalLoader>
  );
};

export default CustomizationEditPageContainer;
